import { useCallback, useEffect, useState } from "react";
import { Autocomplete, Box, Button, CircularProgress, FormControl, TextField, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { useDialog } from "../../../DialogContext";
import { debounce, pageScrollStyle } from "../../../../styles/styles";
import { getAlldocCategory } from "../../../../redux/Apps/Lawyerapp/DocumentSlice/DocumentSlice";
import DocumentCategoriForm from "../../Document/DocumentCategoriForm";
import { AddRounded } from "@mui/icons-material";
import { NoDataOptionField } from "../../../../static/staticdata";
import noOption from "../../../../assets/no-option-found.webp";

const DocCategoryApiField = ({
  name,
  label = "Category",
  value,
  onChange,
  errors,
  token,
  apiUrl,
  isRequired = false,
  renderNoOptions,
  AddCategoryFunc,
  formatApiResponse,
  defaultValue = null,
  editData = null,
  noDataText = "Oops! Looks like there are no categories to display",
  addButtonText = "Add Category",
  placeholder = "Select category",
  searchKey = "category_name",
  teamMemberLawyerId = null,
}) => {
  const dispatch = useDispatch();
  const { showDialog } = useDialog();
  const [apiOptions, setApiOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [nextUrl, setNextUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  // Handle editData
  useEffect(() => {
    if (editData) {
      setApiOptions((prevOptions) => {
        const existingIds = prevOptions.map((option) => option.id);
        if (!existingIds.includes(editData.id)) {
          return [editData, ...prevOptions];
        }
        return prevOptions;
      });
      setSelectedCategory(editData);
    }
  }, [editData]);

  // Set initial selected category based on value prop
  useEffect(() => {
    if (value && apiOptions.length > 0) {
      const selectedOption = apiOptions.find((option) => option.id === value);
      if (selectedOption) {
        setSelectedCategory(selectedOption);
      }
    } else if (!value) {
      setSelectedCategory(null);
    }
  }, [value, apiOptions]);

  const debouncedSearch = useCallback(
    debounce(async (query) => {
      await fetchCategories(query);
    }, 300),
    [apiUrl, token, formatApiResponse]
  );

  const getNextPage = (nextUrl) => {
    if (!nextUrl) return 1;
    const url = new URL(nextUrl);
    return parseInt(url.searchParams.get("page")) || 1;
  };

  const fetchCategories = async (searchQuery = "", isLoadMore = false) => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const result = await dispatch(
        getAlldocCategory({
          url: apiUrl,
          token,
          page: isLoadMore ? getNextPage(nextUrl) : 1,
          // pageSize: 50,
          search: searchQuery,
          searchKey,
          teamMemberLawyerId,
        })
      ).unwrap();

      const resultsData = result?.results || result || [];

      const formatted = formatApiResponse
        ? formatApiResponse({
            res: resultsData,
            api: apiUrl,
          })
        : resultsData;

      const safeFormatted = Array.isArray(formatted) ? formatted : [];

      if (isLoadMore) {
        setApiOptions((prev) => [...(prev || []), ...safeFormatted]);
      } else {
        const combinedOptions = [...safeFormatted];

        if (
          editData &&
          !combinedOptions.find((option) => option.id === editData.id)
        ) {
          combinedOptions.unshift(editData);
        }

        setApiOptions(combinedOptions);
      }

      // Handle pagination if your API returns pagination info
      const paginationData = result?.pagination || {};
      setNextUrl(paginationData.next || null);
    } catch (err) {
      console.error("Fetch categories error:", err);
      setApiOptions([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleInputChange = (_, value) => {
    setSearchText(value || "");
    debouncedSearch(value || "");
  };

  const handleCategorySelectionChange = (_, newValue) => {
    setSelectedCategory(newValue);
    if (onChange) {
      onChange(newValue?.id || null);
    }
  };

  const handleScroll = (e) => {
    const node = e.currentTarget;
    if (
      !isLoading &&
      nextUrl &&
      node.scrollTop + node.clientHeight >= node.scrollHeight - 50
    ) {
      fetchCategories(searchText, true);
    }
  };

  // Handle dropdown open - refresh data when dropdown opens
  const handleOpen = () => {
    setIsOpen(true);
    // Refresh data when dropdown opens to get latest options
    fetchCategories(searchText);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  // Handle input focus - refresh data when input gets focus
  const handleFocus = () => {
    // Refresh data when input gets focus
    fetchCategories(searchText);
  };

  const handleCreateCategory = () => {
    showDialog(<DocumentCategoriForm />, {
      dialogTitle: "Create New Category",
    });
  };

  const ListboxComponent = (props) => {
    const { children, ...rest } = props;
    return (
      <Box sx={{ position: "relative" }}>
        <Box {...rest} sx={{ overflowY: "auto", ...pageScrollStyle }}>
          {children}
        </Box>

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            borderTop: "1px solid #e5e5e5",
            backgroundColor: "white",
            p: 1,
            display: "flex",
            justifyContent: "flex-end",
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleCreateCategory();
            }}
            variant="text"
            size="small"
            color="primary"
            endIcon={<AddRounded />}
          >
            {addButtonText}
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <FormControl fullWidth size="small">
      <Autocomplete
        fullWidth
        open={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        options={Array.isArray(apiOptions) ? apiOptions : []}
        inputValue={searchText}
        value={selectedCategory}
        onInputChange={handleInputChange}
        onChange={handleCategorySelectionChange}
        loading={isLoading}
        getOptionLabel={(option) => {
          if (!option) return "";
          return option.category_name || "";
        }}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        ListboxProps={{
          onScroll: handleScroll,
          style: { maxHeight: "250px", overflowY: "auto" },
        }}
        noOptionsText={
          renderNoOptions || (
            <NoDataOptionField
              imgSrc={noOption}
              text={noDataText}
              btnText={addButtonText}
              handleClickNoData={handleCreateCategory}
            />
          )
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={
              <span>
                {label}
                {isRequired && <span style={{ color: "red" }}> *</span>}
              </span>
            }
            placeholder={placeholder}
            size="small"
            fullWidth
            error={Boolean(errors?.[name])}
            helperText={errors?.[name]?.message}
            onFocus={handleFocus}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading && <CircularProgress color="primary" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        ListboxComponent={ListboxComponent}
        disablePortal={false}
        PopperProps={{ container: document.body, sx: { zIndex: 14000 } }}
        renderOption={(props, option) => (
          <li
            {...props}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <Typography variant="body2" fontWeight="medium">
                {option.category_name}
              </Typography>
              {option?.description && (
                <Typography variant="caption" color="textSecondary">
                  {option.description}
                </Typography>
              )}
            </Box>
          </li>
        )}
      />
    </FormControl>
  );
};

export default DocCategoryApiField
